import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, ArrowUpRight, Atom, Layers, Recycle, ArrowDown,
  Mountain, Flame, Hexagon, GitMerge, Grid3x3, ChevronRight,
} from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { ASSETS, SITE_URL, CONTACT, FEATURE_BASE } from '../data/site';
import { SectionLabel } from '../components/PageHeader';
import { Reveal } from '../components/Reveal';
import SEO from '../components/SEO';
import { SEO_KEYWORDS } from '../data/seo';
import { track } from '../lib/analytics';
import semImage from '../assets/metakaolin-sem.jpg';
import semImageO from '../assets/metaver-sem-mpa.jpg';

const benefitIcons = [Atom, Layers, Recycle];
const topicLinks = ['/reaktion', '/qualitaet-pruefung', '/eigenschaften', '/zementsubstitution'];
const propSlugs = ['setting-time', 'pore-size', 'chloride-migration', 'sulphate-resistance', 'freeze-thaw', 'asr-reduction', 'lighter-colour', 'efflorescence'];
const processIcons = [Mountain, Flame, Hexagon, GitMerge, Grid3x3];

const Home = () => {
  const { t, lang } = useLang();
  const benefits = t('home.benefits');
  const topics = t('home.topics');
  const props = t('home.props');
  const process = t('home.process');
  const apps = t('home.apps');

  const seoTitle = lang === 'de'
    ? 'Metakaolin f\u00fcr Beton und M\u00f6rtel | Wissenschaftliche Informationen'
    : 'Metakaolin for concrete and mortar | Scientific information';
  const seoDesc = lang === 'de'
    ? 'Wissenschaftlich fundierte Informationen zu Metakaolin: Reaktivit\u00e4t, Qualit\u00e4tskriterien, Dauerhaftigkeit, Zementsubstitution und Anwendungen in Beton und M\u00f6rtel.'
    : 'Scientifically grounded information on metakaolin: reactivity, quality criteria, durability, cement substitution and applications in concrete and mortar.';

  const jsonLd = [
    { '@context': 'https://schema.org', '@type': 'Organization', name: 'Newchem GmbH', url: CONTACT.website, logo: ASSETS.logo,
      address: { '@type': 'PostalAddress', streetAddress: CONTACT.street, addressLocality: 'Baden bei Wien', postalCode: '2500', addressCountry: 'AT' },
      email: CONTACT.email, telephone: CONTACT.phone },
    { '@context': 'https://schema.org', '@type': 'WebSite', name: 'metakaolin.info', url: SITE_URL, inLanguage: ['de', 'en'],
      publisher: { '@type': 'Organization', name: 'Newchem GmbH' } },
    { '@context': 'https://schema.org', '@type': 'DefinedTerm', name: 'Metakaolin',
      description: 'Metakaolin ist ein durch Kalzinierung von Kaolin hergestellter, \u00fcberwiegend amorpher alumosilikatischer Zusatzstoff f\u00fcr zementgebundene Baustoffe.',
      inDefinedTermSet: SITE_URL },
  ];

  return (
    <div>
      <SEO title={seoTitle} description={seoDesc} path="/" lang={lang} keywords={SEO_KEYWORDS[lang]} jsonLd={jsonLd} />

      {/* HERO \u2013 calm material world + subtle microstructure */}
      <section className="relative overflow-hidden bg-white border-b border-slate-100">
        <div className="absolute inset-0 -z-0" aria-hidden="true">
          <img src={ASSETS.powderPile} alt="" className="w-full h-full object-cover opacity-90" />
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/95 to-white/70" />
          <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-transparent to-white" />
          {/* subtle scientific micro-structure dot grid */}
          <svg className="absolute inset-0 w-full h-full opacity-[0.5]" aria-hidden="true">
            <defs>
              <pattern id="mkdots" width="26" height="26" patternUnits="userSpaceOnUse">
                <circle cx="1.4" cy="1.4" r="1.4" fill="#1d4ed8" fillOpacity="0.12" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#mkdots)" />
          </svg>
        </div>
        <div className="relative container-wide py-20 lg:py-28">
          <div className="max-w-3xl mk-fade-up">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-blue-600" aria-hidden="true" />
              <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-blue-700">{t('home.eyebrow')}</p>
            </div>
            <h1 className="text-[34px] sm:text-[46px] lg:text-[56px] font-extrabold text-slate-900 leading-[1.06] tracking-tight" style={{ textWrap: 'balance' }}>
              {t('home.title')}
            </h1>
            <p className="mt-6 text-[16px] sm:text-lg text-slate-700 leading-relaxed max-w-2xl">{t('home.subtitle')}</p>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Link to="/grundlagen" className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-blue-700 text-white text-[14px] font-semibold hover:bg-blue-800 transition-colors">
                {t('home.primary')} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/eigenschaften" className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-white border border-slate-300 text-slate-800 text-[14px] font-semibold hover:border-blue-400 hover:text-blue-700 transition-colors">
                {t('home.secondary')}
              </Link>
              <Link to="/qualitaet-pruefung" className="inline-flex items-center gap-1.5 px-2 py-3 text-[14px] font-semibold text-blue-700 hover:text-blue-800 transition-colors">
                {t('home.tertiary')} <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 3 PRINCIPLE CARDS */}
      <section className="bg-white py-14 lg:py-16 border-b border-slate-100">
        <div className="container-wide grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6">
          {benefits.map((b, i) => {
            const Icon = benefitIcons[i] || Atom;
            return (
              <Reveal key={i} delay={i * 90}>
                <article className="h-full flex flex-col gap-3 p-6 lg:p-7 rounded-xl bg-white border border-slate-200 hover:border-blue-300 hover:shadow-md hover:shadow-blue-700/5 transition">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex items-center justify-center w-11 h-11 rounded-lg bg-blue-700/5 border border-blue-700/15 text-blue-700"><Icon className="w-5 h-5" /></div>
                    <span className="text-[10px] font-bold tracking-[0.22em] text-blue-700/60">0{i + 1}</span>
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 leading-snug">{b.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{b.desc}</p>
                </article>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* OVERVIEW */}
      <section className="bg-slate-50 border-b border-slate-200 py-16 lg:py-20">
        <div className="container-wide grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <SectionLabel>{t('home.overviewLabel')}</SectionLabel>
            <h2 className="mt-5 text-[26px] sm:text-[32px] font-extrabold tracking-tight text-slate-900 leading-tight">{t('home.overviewTitle')}</h2>
            <p className="mt-5 text-[15px] text-slate-600 leading-relaxed max-w-2xl">{t('home.overviewBody')}</p>
            <div className="mt-6 flex flex-wrap gap-4">
              <Link to="/grundlagen" className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-blue-700 hover:text-blue-800">{t('home.overviewLink1')} <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/reaktion" className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-blue-700 hover:text-blue-800">{t('home.overviewLink2')} <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
              <img src={ASSETS.powderMagnifier} alt="Metakaolin" className="w-full h-64 object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* SCIENTIFIC TOPICS */}
      <section className="bg-white py-16 lg:py-20 border-b border-slate-100">
        <div className="container-wide">
          <SectionLabel>{t('home.topicsLabel')}</SectionLabel>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5 lg:gap-6">
            {topics.map((tp, i) => (
              <Reveal key={i} delay={(i % 2) * 90}>
                <Link to={topicLinks[i]} className="group flex items-start justify-between gap-4 p-7 rounded-xl bg-white border border-slate-200 hover:border-blue-300 hover:shadow-md hover:shadow-blue-700/5 transition">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 leading-snug group-hover:text-blue-700 transition-colors">{tp.title}</h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{tp.desc}</p>
                  </div>
                  <ArrowUpRight className="w-5 h-5 text-slate-300 group-hover:text-blue-600 shrink-0 transition-colors" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROPERTIES GRID */}
      <section className="bg-slate-50 border-b border-slate-200 py-16 lg:py-20">
        <div className="container-wide">
          <SectionLabel>{t('home.propsLabel')}</SectionLabel>
          <h2 className="mt-5 text-[24px] sm:text-[30px] font-extrabold tracking-tight text-slate-900">{t('home.propsTitle')}</h2>
          <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
            {props.map((p, i) => (
              <Reveal key={i} delay={(i % 4) * 60}>
                <Link to={`${FEATURE_BASE}/${propSlugs[i]}`} className="group flex items-center justify-between gap-2 p-4 lg:p-5 rounded-lg bg-white border border-slate-200 hover:border-blue-300 hover:shadow-sm transition">
                  <span className="text-[14px] font-semibold text-slate-800 group-hover:text-blue-700 transition-colors leading-snug">{p.title}</span>
                  <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-blue-600 shrink-0 transition-colors" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS GRAPHIC */}
      <section className="bg-white py-16 lg:py-20 border-b border-slate-100">
        <div className="container-wide">
          <SectionLabel>{t('home.processLabel')}</SectionLabel>
          <h2 className="mt-5 text-[24px] sm:text-[30px] font-extrabold tracking-tight text-slate-900">{t('home.processTitle')}</h2>
          <div className="mt-10 flex flex-col lg:flex-row items-stretch gap-3 lg:gap-0">
            {process.map((step, i) => {
              const Icon = processIcons[i] || Hexagon;
              return (
                <React.Fragment key={i}>
                  <Reveal delay={i * 80} className="flex-1">
                    <div className="h-full flex flex-col items-center text-center px-3">
                      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-blue-700/5 border border-blue-700/20 text-blue-700"><Icon className="w-6 h-6" /></div>
                      <p className="mt-4 text-[15px] font-bold text-slate-900">{step.title}</p>
                      <p className="mt-1.5 text-[12.5px] text-slate-500 leading-relaxed max-w-[190px]">{step.desc}</p>
                    </div>
                  </Reveal>
                  {i < process.length - 1 && (
                    <div className="flex items-center justify-center lg:pt-2 text-slate-300" aria-hidden="true">
                      <ArrowDown className="w-5 h-5 lg:hidden" />
                      <ChevronRight className="hidden lg:block w-6 h-6" />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* SEM micrograph of platelet structure */}
          <Reveal>
            <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center">
              <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 gap-5">
                <figure>
                  <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm bg-slate-900">
                    <img src={semImage} alt={t('home.semCaption')} width="344" height="344" className="w-full aspect-[4/3] object-cover" data-testid="sem-image" />
                  </div>
                  <figcaption className="mt-2 text-[11.5px] text-slate-500 italic leading-snug">{t('home.semCaption')}</figcaption>
                </figure>
                <figure>
                  <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm bg-slate-900">
                    <img src={semImageO} alt={t('home.semKCaption')} width="882" height="662" className="w-full aspect-[4/3] object-cover" data-testid="sem-image-metaver-o" />
                  </div>
                  <figcaption className="mt-2 text-[11.5px] text-slate-500 italic leading-snug">{t('home.semKCaption')}</figcaption>
                </figure>
              </div>
              <div className="lg:col-span-7">
                <h3 className="text-[20px] sm:text-[24px] font-extrabold tracking-tight text-slate-900">{t('home.semTitle')}</h3>
                <p className="mt-4 text-[15px] text-slate-600 leading-relaxed max-w-2xl">{t('home.semBody')}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* APPLICATIONS */}
      <section className="bg-slate-50 border-b border-slate-200 py-16 lg:py-20">
        <div className="container-wide">
          <SectionLabel>{t('home.appsLabel')}</SectionLabel>
          <h2 className="mt-5 text-[24px] sm:text-[30px] font-extrabold tracking-tight text-slate-900">{t('home.appsTitle')}</h2>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
            {apps.map((a, i) => (
              <Reveal key={i} delay={(i % 3) * 70}>
                <Link to="/anwendungen" className="group flex items-center justify-between gap-3 p-6 rounded-xl bg-white border border-slate-200 hover:border-blue-300 hover:shadow-md hover:shadow-blue-700/5 transition">
                  <span className="text-[15px] font-bold text-slate-900 group-hover:text-blue-700 transition-colors">{a.title}</span>
                  <span className="text-[11px] font-bold tracking-[0.2em] text-blue-700/50">{String(i + 1).padStart(2, '0')}</span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white py-16 lg:py-20">
        <div className="container-wide">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-6 sm:px-10 py-10 lg:py-12 max-w-4xl mx-auto text-center">
            <h2 className="text-[24px] sm:text-[30px] font-extrabold tracking-tight text-slate-900">{t('home.ctaTitle')}</h2>
            <p className="mt-4 text-[15px] text-slate-600 leading-relaxed max-w-2xl mx-auto">{t('home.ctaBody')}</p>
            <Link to="/contact" onClick={() => track('cta_technical_contact', { location: 'home' })} className="mt-7 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-blue-700 text-white text-[14px] font-semibold hover:bg-blue-800 transition-colors">
              {t('home.ctaBtn')} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
